import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { GlobalToastTypeNames, ToastTypes } from 'types';
import {
  useStudentTasksListQuery,
  useGroupTasksListQuery,
  useAgreementTasksListQuery,
  useAddTaskMutation,
  useChangeTaskStatusMutation,
  useRemoveTaskMutation,
  StudentTasksListDocument,
  GroupTasksListDocument,
  AgreementTasksListDocument,
} from 'core/graphql';

import { useToasts } from '../Toast/useToasts';

export interface ITask {
  id: string;
  title: string;
  description?: string | null;
  status: string;
  priority: string;
  dueDate: string | null;
  completedAt?: string | null;
  createdAt?: string;
  assigneeName: string | null;
  agreement?: { id: string; agreementTitle: string | null } | null;
}

interface IUseTasksArgs {
  studentId?: string;
  groupId?: string;
  agreementId?: string;
  includeClosed?: boolean;
}

interface IUseTasks {
  tasks: ITask[];
  loading: boolean;
  saving: boolean;
  add: (args: {
    title: string;
    description?: string;
    dueDate?: string;
    priority?: string;
  }) => Promise<boolean>;
  setStatus: (id: string, status: string) => Promise<boolean>;
  remove: (id: string) => Promise<boolean>;
}

type ErrList =
  | Array<{ message?: string | null; code?: string | null } | null>
  | null
  | undefined;

const errText = (errors: ErrList): string | undefined =>
  errors
    ?.map((e) => e?.message || e?.code || null)
    .filter(Boolean)
    .join('\n') || undefined;

/**
 * Staff tasks for a student OR a group (spec 0003 Phase 2). One of `studentId`
 * / `groupId` selects the query; mutations refetch that list. `includeClosed`
 * toggles whether done/cancelled tasks are shown.
 */
export const useTasks = ({
  studentId,
  groupId,
  agreementId,
  includeClosed = false,
}: IUseTasksArgs): IUseTasks => {
  const { t } = useTranslation();
  const { addToast } = useToasts();

  const studentQuery = useStudentTasksListQuery({
    variables: { studentId: studentId || '', includeClosed },
    skip: !studentId,
    fetchPolicy: 'cache-and-network',
  });
  const groupQuery = useGroupTasksListQuery({
    variables: { groupId: groupId || '', includeClosed },
    skip: !groupId,
    fetchPolicy: 'cache-and-network',
  });
  const agreementQuery = useAgreementTasksListQuery({
    variables: { agreementId: agreementId || '', includeClosed },
    skip: !agreementId,
    fetchPolicy: 'cache-and-network',
  });

  const refetchQueries = useMemo(() => {
    if (studentId) {
      return [
        {
          query: StudentTasksListDocument,
          variables: { studentId, includeClosed },
        },
      ];
    }
    if (groupId) {
      return [
        {
          query: GroupTasksListDocument,
          variables: { groupId, includeClosed },
        },
      ];
    }
    if (agreementId) {
      return [
        {
          query: AgreementTasksListDocument,
          variables: { agreementId, includeClosed },
        },
      ];
    }
    return [];
  }, [studentId, groupId, agreementId, includeClosed]);
  const opts = { refetchQueries, awaitRefetchQueries: true };

  const [addMut, addState] = useAddTaskMutation(opts);
  const [statusMut, statusState] = useChangeTaskStatusMutation(opts);
  const [removeMut, removeState] = useRemoveTaskMutation(opts);

  const notifyError = useCallback(
    (text?: string) => {
      addToast({
        data: {
          type: ToastTypes.error,
          title: t('pages.tasks.failed'),
          text,
        },
        type: GlobalToastTypeNames.FakturowniaMatch,
      });
    },
    [addToast, t],
  );

  const add = useCallback(
    async (args: {
      title: string;
      description?: string;
      dueDate?: string;
      priority?: string;
    }) => {
      try {
        const res = await addMut({
          variables: {
            input: {
              studentId: studentId || undefined,
              groupId: groupId || undefined,
              agreementId: agreementId || undefined,
              title: args.title,
              description: args.description,
              dueDate: args.dueDate || undefined,
              priority: args.priority,
            },
          },
        });
        const p = res.data?.createStaffTask;
        if (p?.success) return true;
        notifyError(errText(p?.customErrors));
        return false;
      } catch (e) {
        notifyError((e as Error).message);
        return false;
      }
    },
    [addMut, studentId, groupId, agreementId, notifyError],
  );

  const setStatus = useCallback(
    async (id: string, status: string) => {
      try {
        const res = await statusMut({ variables: { id, status } });
        const p = res.data?.updateTaskStatus;
        if (p?.success) return true;
        notifyError(errText(p?.customErrors));
        return false;
      } catch (e) {
        notifyError((e as Error).message);
        return false;
      }
    },
    [statusMut, notifyError],
  );

  const remove = useCallback(
    async (id: string) => {
      try {
        const res = await removeMut({ variables: { id } });
        const p = res.data?.deleteStaffTask;
        if (p?.success) return true;
        notifyError(errText(p?.customErrors));
        return false;
      } catch (e) {
        notifyError((e as Error).message);
        return false;
      }
    },
    [removeMut, notifyError],
  );

  let tasks: ITask[] | undefined;
  let loading: boolean;
  if (studentId) {
    tasks = studentQuery.data?.studentTasks as ITask[] | undefined;
    loading = studentQuery.loading;
  } else if (groupId) {
    tasks = groupQuery.data?.groupTasks as ITask[] | undefined;
    loading = groupQuery.loading;
  } else {
    tasks = agreementQuery.data?.agreementTasks as ITask[] | undefined;
    loading = agreementQuery.loading;
  }

  return {
    tasks: tasks || [],
    loading,
    saving: addState.loading || statusState.loading || removeState.loading,
    add,
    setStatus,
    remove,
  };
};
